import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { ENDPOINT } from "./config";
import UsersList from "./UsersList";
import HeaderProcess from "./HeaderProcess";
import TaskPhaseList from "./TaskPhaseList";
import { TASK_PHASE_ORDER } from "./taskPhases";
import { Progress, Result } from "antd";
import { AuthenticatedApiContext } from "../context/AuthenticatedApiContext";
import { DeleteModal } from "./Components/DeleteModal";
import { GoBackButton } from "./Components/GoBackButton";
import { Spinner } from "./Components/Spinner";
import { SettingsDropdown } from "./Components/SettingsDropdown";
import { AddChildEntityModal } from "./Components/AddChildEntityModal";
import {
  ChildEntitiesList,
  ParentEntity,
} from "./Components/ChildEntitiesList";
import { BranchProgress, EntityTabs } from "./Components/BranchProgress";
import { hasWalletError } from "./Components/branchState";

export default function RenderProcess({
  processId,
  isProcessSelected,
  deleteProcess,
  setFeedback,
}) {
  const [data, setData] = useState();
  const [branchData, setBranchData] = useState(null);
  const [isNotFound, setIsNotFound] = useState(false);
  const hasLoaded = useRef(false);
  const { get } = useContext(AuthenticatedApiContext);

  async function fetchProcess(id) {
    const process = await get(`${ENDPOINT}/processes/${id}`);
    process.Tasks.sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
    return process;
  }

  async function fetchAll() {
    let main;
    try {
      main = await fetchProcess(processId);
    } catch (e) {
      if (!hasLoaded.current) {
        setIsNotFound(true);
      }
      return;
    }
    hasLoaded.current = true;
    const plan = main.branch_plan;
    let branch = null;
    if (plan && plan.mode === "create" && plan.process_id) {
      try {
        branch = await fetchProcess(plan.process_id);
      } catch (e) {
        branch = null;
      }
    }
    setData(main);
    setBranchData(branch);
  }

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 1000);

    return () => clearInterval(interval);
  }, [processId]);

  if (isNotFound && !data) {
    return (
      <div
        style={{ maxWidth: "900px", margin: "0 auto", padding: "32px 24px" }}
      >
        <GoBackButton onClick={isProcessSelected} />
        <div
          style={{
            marginTop: "24px",
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "48px",
            border: "1px solid #e5e7eb",
            textAlign: "center",
          }}
        >
          <Result
            status="404"
            title="Company not found"
            subTitle="This company does not exist or has been deleted."
          />
        </div>
      </div>
    );
  }

  if (data)
    return (
      <Process
        data={data}
        branchData={branchData}
        deleteProcess={deleteProcess}
        isProcessSelected={isProcessSelected}
        setFeedback={setFeedback}
      />
    );
  return <Spinner />;
}

function computeProgressPercent(tasks, expectedTasks) {
  const byPhase = {};

  if (expectedTasks && expectedTasks.length > 0) {
    const byName = {};
    for (const task of tasks) {
      byName[task.name] = task;
    }
    for (const expected of expectedTasks) {
      const phase = expected.phase || "Other";
      if (!byPhase[phase]) {
        byPhase[phase] = [];
      }
      byPhase[phase].push(byName[expected.name] || { status: undefined });
    }
  } else {
    for (const task of tasks) {
      const phase = task.phase || "Other";
      if (!byPhase[phase]) {
        byPhase[phase] = [];
      }
      byPhase[phase].push(task);
    }
  }

  let score = 0;
  let phaseCount = 0;
  for (const phase of TASK_PHASE_ORDER) {
    const phaseTasks = byPhase[phase];
    if (!phaseTasks || phaseTasks.length === 0) {
      continue;
    }
    const done = phaseTasks.filter(
      (task) => task.status && task.status !== "pending"
    ).length;
    score += done / phaseTasks.length;
    phaseCount++;
  }

  if (phaseCount === 0) {
    return 0;
  }
  return Math.round((score / TASK_PHASE_ORDER.length) * 100);
}

function Process({
  data: mainData,
  branchData,
  isProcessSelected,
  deleteProcess,
  setFeedback,
}) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [isAddChildModalOpen, setIsAddChildModalOpen] = useState(false);
  const [childrenRefreshKey, setChildrenRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("tasks");
  const [hasAutoSwitched, setHasAutoSwitched] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState("main");
  const [hasPickedEntity, setHasPickedEntity] = useState(false);

  const plan = mainData.branch_plan;
  const isCreatingBranch = Boolean(plan && plan.mode === "create");
  const isBranchRunning = plan && plan.state !== "done";
  const showEntityTabs = isCreatingBranch && isBranchRunning;
  const viewsBranch = showEntityTabs && selectedEntity === "branch";
  const data = viewsBranch && branchData ? branchData : mainData;
  const isWaitingForBranch = viewsBranch && !branchData;

  const isPending = data.status === "pending";
  const hasNoUsers = !data.Users || data.Users.length === 0;

  const expectedTasks = useMemo(() => {
    if (!data.expected_tasks) {
      return null;
    }
    try {
      return JSON.parse(data.expected_tasks);
    } catch (e) {
      return null;
    }
  }, [data.expected_tasks]);

  const isMainPending = mainData.status === "pending";
  const branchSettled = !plan || plan.state === "done";

  useEffect(() => {
    if (!isMainPending && branchSettled && !hasAutoSwitched) {
      setSelectedEntity("main");
      setActiveTab("users");
      setHasAutoSwitched(true);
    }
  }, [isMainPending, branchSettled, hasAutoSwitched]);

  const planState = plan ? plan.state : null;
  const hasBranchData = Boolean(branchData);

  useEffect(() => {
    if (
      isCreatingBranch &&
      planState === "running" &&
      !hasPickedEntity &&
      hasBranchData
    ) {
      setSelectedEntity("branch");
      setActiveTab("tasks");
    }
  }, [isCreatingBranch, planState, hasPickedEntity, hasBranchData]);

  function selectEntity(key) {
    setSelectedEntity(key);
    setHasPickedEntity(true);
    setActiveTab("tasks");
  }

  async function handleRemoveProcess(processId) {
    await deleteProcess(processId);
    setIsDeleteModalOpen(false);
  }

  const hasError = data.Tasks.some((task) => task.status === "error");
  const percent = isPending
    ? computeProgressPercent(data.Tasks, expectedTasks)
    : 100;

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "0 auto",
        padding: "32px 24px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <GoBackButton onClick={isProcessSelected} />

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SettingsDropdown
            onAddChildEntity={() => setIsAddChildModalOpen(true)}
          />

          <button
            onClick={() => setIsDeleteModalOpen(true)}
            onMouseEnter={() => setIsDeleteHovered(true)}
            onMouseLeave={() => setIsDeleteHovered(false)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 20px",
              backgroundColor: isDeleteHovered ? "#dc2626" : "#ef4444",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "white",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            <svg
              style={{ width: "16px", height: "16px" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Delete
          </button>
        </div>
      </div>

      <DeleteModal
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        handleRemoveProcess={handleRemoveProcess}
        processId={mainData.id}
      />

      <AddChildEntityModal
        isOpen={isAddChildModalOpen}
        onClose={() => setIsAddChildModalOpen(false)}
        processId={mainData.id}
        setFeedback={setFeedback}
        onSuccess={() => setChildrenRefreshKey((k) => k + 1)}
      />

      <HeaderProcess data={data} />

      {!viewsBranch && <ParentEntity parent={mainData.parent} />}

      {plan && (plan.state !== "done" || hasWalletError(plan)) && (
        <BranchProgress main={mainData} plan={plan} branch={branchData} />
      )}

      {(!plan || plan.state === "done") && (
        <ChildEntitiesList
          processId={mainData.id}
          refreshKey={childrenRefreshKey}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: showEntityTabs ? "space-between" : "center",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        {showEntityTabs && (
          <EntityTabs
            main={mainData}
            plan={plan}
            selected={selectedEntity}
            onSelect={selectEntity}
          />
        )}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            padding: "3px",
          }}
        >
          <button
            onClick={() => setActiveTab("tasks")}
            style={{
              padding: "5px 14px",
              fontSize: "13px",
              fontWeight: "500",
              borderRadius: "7px",
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor:
                activeTab === "tasks" ? "#7c3aed" : "transparent",
              color: activeTab === "tasks" ? "white" : "#4b5563",
            }}
          >
            Tasks
          </button>
          <button
            onClick={() => !isPending && setActiveTab("users")}
            disabled={isPending}
            style={{
              padding: "5px 14px",
              fontSize: "13px",
              fontWeight: "500",
              borderRadius: "7px",
              border: "none",
              cursor: isPending ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              backgroundColor:
                activeTab === "users" ? "#7c3aed" : "transparent",
              color: isPending
                ? "#d1d5db"
                : activeTab === "users"
                ? "white"
                : "#4b5563",
            }}
          >
            Users
          </button>
        </div>
      </div>

      {isWaitingForBranch && (
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "28px",
            border: "1px solid #e5e7eb",
            color: "#6b7280",
          }}
        >
          {plan.name} is created once {mainData.company_name} is ready.
        </div>
      )}
      {!isWaitingForBranch && activeTab === "tasks" && (
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "28px",
            border: "1px solid #e5e7eb",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#111827",
              margin: "0 0 20px 0",
            }}
          >
            {viewsBranch ? "Creating branch..." : "Creating company..."}
          </h3>
          <Progress
            strokeColor="#7c3aed"
            percent={percent}
            showInfo={true}
            status={isPending ? "active" : hasError ? "exception" : "success"}
            style={{ marginBottom: "20px" }}
          />
          <TaskPhaseList tasks={data.Tasks} expectedTasks={expectedTasks} />
        </div>
      )}
      {!isWaitingForBranch &&
        activeTab === "users" &&
        (data.status === "error" && hasNoUsers ? (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "48px",
              border: "1px solid #e5e7eb",
              textAlign: "center",
            }}
          >
            <Result
              status="404"
              title="404"
              subTitle="Sorry, we couldn't find any user. Contact the dev team on #help_demo_account if it's unintended."
            />
          </div>
        ) : (
          <UsersList users={data.Users} />
        ))}
    </div>
  );
}
